<template>
  <div
    data-qa="search-bar"
    class="search-bar"
  >
    <div class="container">
      <form
        class="search-bar__container"
        :style="{ zIndex: isDialogVisible ? 5 : undefined }"
        @submit.prevent="handleSubmitForm"
      >
        <SearchInput
          ref="input"
          v-model="query"
          class="search-bar__input"
          :placeholder="`Врачи, клиники, услуги в ${context.town.name_pred}`"
          @click="handleClickSearchInput"
          @focus="handleFocusSearchInput"
          @submit="handleSubmitForm"
          @keydown.native.up.prevent="handleKeydownArrowUp"
          @keydown.native.down.prevent="handleKeydownArrowDown"
          @keydown.native.esc.prevent="handleKeydownEsc"
        />

        <div
          v-if="!isMobile && isDialogVisible"
          class="search-bar__dropdown-wrapper"
        >
          <div class="overflow-hidden rounded-sm">
            <v-card
              class="search-bar__dropdown"
              rounded="sm"
            >
              <SearchResults
                ref="searchResults"
                :query="query"
                :town="town"
                :is-office-ip="context.is_office_ip"
                x-space-class="px-4"
                @activate-dropdown-item="handleActivateDropdownItem"
                @deactivate-dropdown-item="handleDeactivateDropdownItem"
              />
            </v-card>
          </div>
        </div>
      </form>

      <v-overlay
        v-if="!isMobile"
        v-model="isDialogVisible"
        :opacity="0"
        z-index="1"
        @click="handleClickOverlay"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { ContextStore } from 'src/layouts/core-layout/types'
import { useURLStore, useContextStore } from 'src/store'
import { Location } from 'src/layouts/core-layout/components/CoreSearch/types'
import buildSearchUrl from 'src/layouts/core-layout/components/CoreSearch/functions/buildSearchUrl'
import SearchInput from 'src/layouts/core-layout/components/CoreSearch/components/SearchInput.vue'
import SearchResults from 'src/layouts/core-layout/components/CoreSearch/components/SearchResults.vue'
import redirect from 'src/utils/redirect'
import sendYandexGoal from 'src/utils/sendYaGoal'

const { context }: ContextStore = useContextStore()
const { reverseFull } = useURLStore()

const town = {
  id: context.town.id,
  name: context.town.name,
  name_pred: context.town.name_pred,
  translit: context.town.translit,
}

// @ts-ignore
const location: Location | undefined = context.metro || context.district
  ? {
    type: context.metro ? 'metro' : 'district',
    name: context.metro ? context.metro.translit : context.district?.translit,
  }
  : undefined

const emit = defineEmits(['click-search-btn'])

const isMobile = context.flavour === 'mobile'

const input = ref<InstanceType<typeof SearchInput>>()
const searchResults = ref<InstanceType<typeof SearchResults> & {
  moveUp:() => void
  moveDown: () => void
}>()

const query = ref('')
const isDialogVisible = ref(false)
const dropdownItemLink = ref('')

function handleClickSearchInput() {
  if (isMobile) {
    isDialogVisible.value = true

    emit('click-search-btn')
  }
}

function handleFocusSearchInput() {
  if (!isMobile) {
    isDialogVisible.value = true

    emit('click-search-btn')

    if (!context.is_office_ip) {
      sendYandexGoal('search_focus')
    }
  }
}

function handleClickOverlay() {
  isDialogVisible.value = false
}

function handleKeydownArrowUp() {
  searchResults.value?.moveUp()
}

function handleKeydownArrowDown() {
  searchResults.value?.moveDown()
}

function handleKeydownEsc(event: Event) {
  isDialogVisible.value = false;
  (event.target as HTMLInputElement).blur()
}

function handleActivateDropdownItem({ title, link }: { title?: string, link?: string }) {
  query.value = title ?? ''
  dropdownItemLink.value = link ?? ''
}

function handleDeactivateDropdownItem() {
  dropdownItemLink.value = ''
}

function handleSubmitForm() {
  let redirectUrl = ''

  if (dropdownItemLink.value) {
    if (!context.is_office_ip) {
      sendYandexGoal('oldSearch_dropdownItem')
    }

    redirectUrl = dropdownItemLink.value
  } else if (query.value) {
    if (!context.is_office_ip) {
      sendYandexGoal('oldSearch_searchButton')
    }

    redirectUrl = buildSearchUrl({
      query: query.value,
      searchUrl: reverseFull({ name: 'find', args: [context.town.translit] }),
      location,
    })
  }

  if (redirectUrl) {
    redirect({ path: redirectUrl })
  }
}
</script>

<style scoped lang="scss">
@import 'src/styles/index';
@import 'src/styles/typography';
@import 'src/styles/breakpoints';
@import 'src/styles/variables';

.search-bar {
  &__container {
    position: relative;
    display: flex;
    border-radius: $border-radius-sm;
    background-color: $ui-kit-bg-gray-0;
  }

  &__input {
    flex-grow: 1;
  }

  &__dropdown-wrapper {
    position: absolute;
    left: 0;
    right: 0;
    top: 56px;
    border-radius: $border-radius-sm;

    @include elevation-2;
  }

  &__dropdown {
    overflow: auto;
    max-height: 480px;
  }
}
</style>
